"use client";

import { useFirebaseStore } from "@/stores/firebase.store";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { redirect } from "next/navigation";
import { useEffect, useState } from "react";

interface AuthValidatorProps {
	children: React.ReactNode;
}

export const AuthValidator = ({ children }: AuthValidatorProps) => {
	const [authState, setAuthState] = useState<{
		isReady: boolean;
		isAuthenticated: boolean | null;
	}>({ isReady: false, isAuthenticated: null });
	const { getFirebaseApp } = useFirebaseStore((state) => ({
		getFirebaseApp: state.getFirebaseApp,
	}));

	useEffect(() => {
		let unsubscribe = () => {};
		let isCancelled = false;

		const initAuth = async () => {
			try {
				const firebaseApp = await getFirebaseApp();
				if (isCancelled) return;

				const auth = getAuth(firebaseApp);
				unsubscribe = onAuthStateChanged(auth, (user) => {
					setAuthState({ isReady: true, isAuthenticated: !!user });
				});
			} catch (error) {
				console.error("Failed to initialize Firebase:", error);
				setAuthState((prev) => ({ ...prev, isReady: true }));
			}
		};

		initAuth();

		return () => {
			isCancelled = true;
			unsubscribe();
		};
	}, [getFirebaseApp]);

	if (!authState.isReady || authState.isAuthenticated === null) {
		return null;
	}

	if (!authState.isAuthenticated) {
		redirect("/sign-in");
	}

	return <>{children}</>;
};
